// /forge/bod-vote — BOD Vote phase page.
//
// Board-vote slate, approval history, vote→training time, and deferral
// patterns. Upcoming Slate tab now has a live cohort count; other tabs
// remain placeholders until upstream data lands.

import { useEffect, useState } from "react";
import PhaseTabs, { PlaceholderTab } from "../../components/shared/PhaseTabs";
import { usePipeline } from "../../hooks/useDashboard";
import KPICard from "../../components/dashboard/KPICard";

function UpcomingSlate() {
  const { data, isLoading } = usePipeline();
  const rows = data?.pipeline ?? [];

  // Stages considered "cleared and queued for BOD" — matches the STAGE_MAP
  // in Pipeline Overview. Anything Physical Passed or earlier isn't ready for
  // the slate; BOD Approved/Complete has already voted.
  const slateStages = new Set([
    "Physical Passed",
    "Physical",
    "Physical Exam",
    "OEMS Eligible",
    "OEMS",
  ]);
  const approvedStages = new Set(["BOD", "BOD Approved", "Board Approved"]);
  const completeStages = new Set(["Complete", "Completed"]);

  const queued = rows
    .filter((r) => r.pipeline_stage && slateStages.has(r.pipeline_stage))
    .reduce((a, r) => a + r.count, 0);
  const approved = rows
    .filter((r) => r.pipeline_stage && approvedStages.has(r.pipeline_stage))
    .reduce((a, r) => a + r.count, 0);
  const complete = rows
    .filter((r) => r.pipeline_stage && completeStages.has(r.pipeline_stage))
    .reduce((a, r) => a + r.count, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPICard
          label="Queued for BOD"
          value={isLoading ? "—" : queued.toString()}
          subtitle="Cleared through physicals + OEMS, awaiting board vote"
        />
        <KPICard
          label="Approved, pre-training"
          value={isLoading ? "—" : approved.toString()}
          subtitle="BOD voted in, not yet started recruit school"
          accent={approved > 0 ? "green" : "neutral"}
        />
        <KPICard
          label="Pipeline Complete"
          value={isLoading ? "—" : complete.toString()}
          subtitle="Full pipeline completed; moved to Year 1 Active"
          accent="neutral"
        />
      </div>
      <div className="glass rounded-xl p-4 text-sm text-theme-secondary">
        <p className="font-semibold mb-1">Current slate composition</p>
        <p className="text-theme-muted">
          Aggregate counts only — no names. For per-applicant coordinator work
          and the <strong>BOD vote workflow itself</strong> (per-applicant
          background check, references, training progress), use{" "}
          <a href="/applicants" className="text-brand-adaptive hover:underline">
            Applicant Tracker
          </a>
          .
        </p>
        <p className="text-theme-muted mt-2">
          Approval history (decline / defer / pass-through trends),
          vote-to-training time, and deferral aging are tracked in the
          placeholder tabs below — the data model is being built out from BOD
          minutes parsing; tabs go live as the backfill lands.
        </p>
      </div>
    </div>
  );
}

interface BodHistoryMonthly {
  ym: string;
  approved: number;
  declined: number;
  archived: number;
  total: number;
}

interface BodHistoryResponse {
  monthly: BodHistoryMonthly[];
  summary: {
    total_voted: number;
    approval_rate: number;
    decline_rate: number;
    archive_rate: number;
    oldest_ym: string | null;
    newest_ym: string | null;
  };
  trailing_12_months: {
    approved: number;
    declined: number;
    approval_rate: number;
    months_present: number;
  };
}

function ApprovalHistory() {
  const [data, setData] = useState<BodHistoryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/forge/bod-history", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`/api/forge/bod-history → ${r.status}`);
        return (await r.json()) as BodHistoryResponse;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 px-4 py-3 text-sm">
        {err}
      </div>
    );
  }
  if (!data) return <p className="text-sm text-theme-muted">Loading…</p>;
  if (data.monthly.length === 0) {
    return (
      <p className="text-sm text-theme-muted">
        No historical BOD votes recorded.
      </p>
    );
  }

  // Render months newest first in the table, but the chart goes left-to-right.
  const monthsAsc = data.monthly;
  const monthsDesc = [...monthsAsc].reverse();
  const maxN = Math.max(1, ...monthsAsc.map((m) => m.total));
  const pct = (n: number, d: number) =>
    d > 0 ? `${((n / d) * 100).toFixed(1)}%` : "—";

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPICard
          label="Lifetime votes"
          value={String(data.summary.total_voted)}
          subtitle={
            data.summary.oldest_ym
              ? `since ${data.summary.oldest_ym}`
              : undefined
          }
        />
        <KPICard
          label="Lifetime approval rate"
          value={pct(data.summary.approval_rate * 100, 100)}
          subtitle="approved / (approved + declined)"
          accent={data.summary.approval_rate >= 0.9 ? "green" : "neutral"}
        />
        <KPICard
          label="Lifetime archive rate"
          value={pct(data.summary.archive_rate * 100, 100)}
          subtitle="withdrew / physical-fail / other"
          accent={data.summary.archive_rate > 0.25 ? "amber" : "neutral"}
        />
        <KPICard
          label="Trailing 12mo approval"
          value={pct(data.trailing_12_months.approval_rate * 100, 100)}
          subtitle={`${data.trailing_12_months.approved}/${data.trailing_12_months.approved + data.trailing_12_months.declined} of last ${data.trailing_12_months.months_present}mo`}
        />
      </div>

      {/* Stacked bar chart — pure CSS, last 24 months */}
      <section className="glass rounded-xl p-4">
        <h3 className="text-sm font-semibold text-theme-secondary mb-3">
          Outcomes by month — last {Math.min(24, monthsAsc.length)} months
        </h3>
        <div className="flex items-end gap-1 h-40">
          {monthsAsc.slice(-24).map((m) => {
            const h = (n: number) => `${(n / maxN) * 100}%`;
            return (
              <div
                key={m.ym}
                className="flex-1 flex flex-col justify-end"
                title={`${m.ym}: ${m.approved} approved · ${m.declined} declined · ${m.archived} archived`}
              >
                <div
                  style={{ height: h(m.archived) }}
                  className="bg-zinc-500/40"
                />
                <div
                  style={{ height: h(m.declined) }}
                  className="bg-red-500/60"
                />
                <div
                  style={{ height: h(m.approved) }}
                  className="bg-emerald-500/60"
                />
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-theme-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-emerald-500/60" />
            Approved
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-red-500/60" />
            Declined
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-zinc-500/40" />
            Archived (withdrew / physical-fail)
          </span>
        </div>
        <p className="text-xs text-theme-muted mt-1.5 italic">
          Bar height is normalized within the period. Hover a bar for raw
          counts.
        </p>
      </section>

      {/* Detail table — newest first */}
      <section className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-theme-muted uppercase tracking-wider border-b border-theme-subtle">
                <th className="text-left  py-2.5 px-4 font-semibold">Month</th>
                <th className="text-right py-2.5 px-4 font-semibold">
                  Approved
                </th>
                <th className="text-right py-2.5 px-4 font-semibold">
                  Declined
                </th>
                <th className="text-right py-2.5 px-4 font-semibold">
                  Archived
                </th>
                <th className="text-right py-2.5 px-4 font-semibold">Total</th>
                <th className="text-right py-2.5 px-4 font-semibold">
                  Approval %
                </th>
              </tr>
            </thead>
            <tbody>
              {monthsDesc.slice(0, 36).map((m) => (
                <tr
                  key={m.ym}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="py-2 px-4 font-mono">{m.ym}</td>
                  <td className="py-2 px-4 text-right tabular-nums text-emerald-400">
                    {m.approved}
                  </td>
                  <td className="py-2 px-4 text-right tabular-nums text-red-400">
                    {m.declined}
                  </td>
                  <td className="py-2 px-4 text-right tabular-nums text-theme-muted">
                    {m.archived}
                  </td>
                  <td className="py-2 px-4 text-right tabular-nums">
                    {m.total}
                  </td>
                  <td className="py-2 px-4 text-right tabular-nums">
                    {m.approved + m.declined > 0
                      ? `${((m.approved / (m.approved + m.declined)) * 100).toFixed(0)}%`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default function BODVote() {
  return (
    <PhaseTabs
      eyebrow="FORGE · BOD Vote"
      title="BOD Vote"
      subtitle="Board decision point — what's on the upcoming slate and how past votes flowed."
      tabs={[
        {
          key: "slate",
          label: "Upcoming Slate",
          content: <UpcomingSlate />,
        },
        {
          key: "history",
          label: "Approval History",
          content: <ApprovalHistory />,
        },
        {
          key: "vote-to-training",
          label: "Vote → Training Time",
          placeholder: true,
          content: (
            <PlaceholderTab hint="Time from BOD approval to Recruit School / AIS start, by cohort. Identifies scheduling bottlenecks between approval and onboarding." />
          ),
        },
        {
          key: "deferrals",
          label: "Deferrals",
          placeholder: true,
          content: (
            <PlaceholderTab hint="Deferred applicants, reasons, and re-slate timing. Trend of deferral rate + aging." />
          ),
        },
      ]}
    />
  );
}
